import React, { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAppDispatch } from '../../store/hooks';
import { loginSuccess } from '../../store/slices/authSlice';
import ReactChatbot, { EmbedLang, EmbedInitialView } from '../ReactChatbot/ReactChatbot';
import { resolveTheme, themeToCssVars } from '../../config/embedThemes';

const SUPPORTED_LANGS: EmbedLang[] = ['es', 'en'];
const SUPPORTED_INITIAL_VIEWS: EmbedInitialView[] = ['chat', 'list'];

const normalizeLang = (raw: string | null): EmbedLang => {
  if (!raw) return 'en';
  const value = raw.trim().toLowerCase();
  return (SUPPORTED_LANGS as string[]).includes(value) ? (value as EmbedLang) : 'en';
};

const normalizeInitialView = (raw: string | null): EmbedInitialView => {
  if (!raw) return 'chat';
  const value = raw.trim().toLowerCase();
  return (SUPPORTED_INITIAL_VIEWS as string[]).includes(value) ? (value as EmbedInitialView) : 'chat';
};

const ReactChatbotEmbed: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();

  const [authReady, setAuthReady] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  // Read params once on mount; URL is cleaned via replaceState below
  const params = useMemo(() => {
    return {
      token: searchParams.get('token'),
      sectionCode: searchParams.get('sectionCode'),
      lang: normalizeLang(searchParams.get('lang')),
      initialView: normalizeInitialView(searchParams.get('initialView')),
      theme: searchParams.get('theme'),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const themeStyle = useMemo(
    () => themeToCssVars(resolveTheme(params.theme)),
    [params.theme]
  );

  // Hydrate auth from URL token
  useEffect(() => {
    if (params.token) {
      dispatch(
        loginSuccess({
          token: params.token,
          tenantId: '',
          userId: '',
          userName: '',
          name: '',
          tenantType: '',
          permissions: [],
        })
      );
    } else {
      // No token provided → treat as already-expired session
      setSessionExpired(true);
    }

    // Strip query params from URL so the token is no longer visible
    if (window.history.replaceState) {
      window.history.replaceState({}, '', window.location.pathname);
    }

    setAuthReady(true);
  }, [dispatch, params.token]);

  // Listen for session expiration triggered by Apollo on 401/409
  useEffect(() => {
    const handler = () => setSessionExpired(true);
    window.addEventListener('embed-session-expired', handler);
    return () => window.removeEventListener('embed-session-expired', handler);
  }, []);

  if (!authReady) {
    return null;
  }

  return (
    <div style={{ ...themeStyle, height: '100vh', width: '100%' }}>
      <ReactChatbot
        embedded
        sectionCode={params.sectionCode}
        sessionExpired={sessionExpired}
        lang={params.lang}
        initialView={params.initialView}
      />
    </div>
  );
};

export default ReactChatbotEmbed;
